import { useState } from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface ChunkLoadingFallbackProps {
  error?: Error;
  onRetry?: () => void;
}

export function ChunkLoadingFallback({ error, onRetry }: ChunkLoadingFallbackProps) {
  const [isRetrying, setIsRetrying] = useState(false);

  const isChunkError = error?.message?.includes('Failed to fetch dynamically imported module') ||
                       error?.message?.includes('Importing a module script failed') ||
                       error?.message?.includes('error loading dynamically imported module');

  const handleRetry = async () => {
    setIsRetrying(true);
    
    if (onRetry) {
      onRetry();
    } else {
      // PWA disabled - simplified reload without service worker logic
      // Just do a hard reload
      window.location.reload();
    }
  };

  if (!error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-[400px] p-4">
      <Alert variant={isChunkError ? "default" : "destructive"} className="max-w-lg">
        <AlertCircle className="h-5 w-5" />
        <AlertTitle className="text-lg font-semibold">
          {isChunkError ? "Update Available" : "Something went wrong"}
        </AlertTitle>
        <AlertDescription className="space-y-4">
          {isChunkError ? (
            <>
              <p className="text-sm">
                A new version of the application is available. Please refresh to get the latest updates.
              </p>
              <p className="text-xs text-muted-foreground">
                This happens when the app is updated while you're using it.
              </p>
            </>
          ) : (
            <p className="text-sm">
              {error.message || "An unexpected error occurred while loading this page."}
            </p>
          )}
          <Button 
            onClick={handleRetry}
            disabled={isRetrying}
            className="w-full gap-2"
            variant={isChunkError ? "default" : "outline"}
          >
            {isRetrying ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Refreshing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                {isChunkError ? "Refresh Now" : "Try Again"}
              </>
            )}
          </Button>
        </AlertDescription>
      </Alert>
    </div>
  );
}
